/* The overflow menu behind the dots.
 *
 * Written rather than imported, like the router: it is a popup, a focus trap
 * of one, and a keyboard map, and the dependency that provides it also
 * provides forty things this application will never use.
 *
 * Destructive items confirm inside the menu rather than in a dialog. Ending a
 * deployment from a list is exactly the click someone makes by accident, but a
 * modal for it is heavy, steals focus, and on a phone covers the row you were
 * looking at -- so the item turns into its own question and answers it in
 * place, one thumb's width from where the finger already was.
 */
import { useEffect, useId, useRef, useState } from 'react'

export interface MenuAction {
  label: string
  /** Shown instead of `label` once the item has been pressed once. Presence of
   *  this is what makes an item two-step. */
  confirm?: string
  /** Explains the consequence while the confirmation is showing. */
  detail?: string
  danger?: boolean
  disabled?: boolean
  onSelect: () => void
}

export function Menu({
  actions, label, busy,
}: {
  actions: MenuAction[]
  /** For screen readers: whose menu this is. */
  label: string
  busy?: boolean
}) {
  const [open, setOpen] = useState(false)
  const [asking, setAsking] = useState<number | null>(null)
  const wrap = useRef<HTMLDivElement>(null)
  const btn = useRef<HTMLButtonElement>(null)
  const id = useId()

  const close = (restoreFocus = true) => {
    setOpen(false)
    setAsking(null)
    if (restoreFocus) btn.current?.focus()
  }

  useEffect(() => {
    if (!open) return
    const onDown = (e: PointerEvent) => {
      if (!wrap.current?.contains(e.target as Node)) close(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.stopPropagation(); close() }
    }
    // Capture, so a click on something that stops propagation still shuts it.
    document.addEventListener('pointerdown', onDown, true)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('pointerdown', onDown, true)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  // Focus the first item on open, so the keyboard path is not "tab through the
  // whole page again".
  const items = useRef<(HTMLButtonElement | null)[]>([])
  useEffect(() => {
    if (open) items.current.find(Boolean)?.focus()
  }, [open])

  const move = (from: number, dir: 1 | -1) => {
    const live = items.current.filter(Boolean) as HTMLButtonElement[]
    if (!live.length) return
    const at = live.indexOf(items.current[from] as HTMLButtonElement)
    live[(at + dir + live.length) % live.length]!.focus()
  }

  const pick = (i: number, a: MenuAction) => {
    if (a.confirm && asking !== i) { setAsking(i); return }
    close()
    a.onSelect()
  }

  return (
    <div className="menu" ref={wrap}>
      <button
        ref={btn}
        className="menu-dots"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        aria-label={label}
        disabled={busy}
        onClick={() => (open ? close() : setOpen(true))}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            setOpen(true)
          }
        }}
      >
        {busy ? (
          <span className="menu-spin" aria-hidden="true" />
        ) : (
          <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true" focusable="false">
            <circle cx="8" cy="3" r="1.5" fill="currentColor" />
            <circle cx="8" cy="8" r="1.5" fill="currentColor" />
            <circle cx="8" cy="13" r="1.5" fill="currentColor" />
          </svg>
        )}
      </button>

      {open && (
        <div className="menu-pop" id={id} role="menu" aria-label={label}>
          {actions.map((a, i) => {
            const arming = asking === i
            return (
              <div key={a.label} className={arming ? 'menu-arming' : undefined}>
                <button
                  ref={(el) => { items.current[i] = el }}
                  role="menuitem"
                  className={`menu-item${a.danger ? ' danger' : ''}${arming ? ' arming' : ''}`}
                  disabled={a.disabled}
                  onClick={() => pick(i, a)}
                  onKeyDown={(e) => {
                    if (e.key === 'ArrowDown') { e.preventDefault(); move(i, 1) }
                    if (e.key === 'ArrowUp') { e.preventDefault(); move(i, -1) }
                  }}
                >
                  {arming ? a.confirm : a.label}
                </button>
                {arming && (
                  <>
                    {a.detail && <p className="menu-detail">{a.detail}</p>}
                    <button className="menu-item menu-cancel" role="menuitem"
                      onClick={() => setAsking(null)}>
                      Cancel
                    </button>
                  </>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
